import { ReactElement, useMemo } from 'react'
import { useParams } from 'react-router'
import { buildErrorMessage } from '@constants'
import { signedDistanceInDirectionOf } from '@geometry'
import { AxiosError } from 'axios'
import { find, round } from 'lodash-es'
import yup from 'yup'
import { Stack } from '@mui/material'
import { useMutation } from '@tanstack/react-query'
import MagicButton from '@ui/actions/MagicButton'
import { Errors, Form, UnitConvertedField } from '@ui/forms'
import { enqueueSnackbar } from '@ui/snackbar'
import { useModelStore } from '@editorStores'
import { useResultsInvalidation } from '@editorHooks'
import { getElementLoads, getModel, getVerticalTransmissionGraph } from '@queries'
import { interpolate } from '@utils'
import { createVerticalRoofSlab } from '@mutations'
import client from 'src/state/client'
import { createHipRoofShortSidePositions } from './createHipRoofShortSidePositions'
import { createVerticalRoofSlabPayload } from './createVerticalRoofSlabPayload'
import {
  DEFAULT_MIN_POSITION_WIDTH,
  DEFAULT_OFFSET,
  DEFAULT_SPACING,
  stdGeneralizedPositionSchema,
} from './schema'

interface Props {
  selectedElement: string
  isLoading?: boolean
}

// Schema for roof slab validation - empty for now, ready for future implementation

type RoofSlabFormData = yup.InferType<typeof stdGeneralizedPositionSchema>

const roundPointsXY = (points: Point[]) => {
  // round away floating point errors
  return points.map(point => ({
    x: round(point.x, 8),
    y: round(point.y, 8),
    z: point.z,
  }))
}

const createGeneralizedPositions = (
  roofSlab: ShapeObject,
  offsetStart: number,
  offsetEnd: number,
  spacing: number,
  minPositionWidth: number,
) => {
  const slabsToReturn = []
  const spacingHalf = spacing / 2
  const p1 = roofSlab.shape.points[0]
  const p2 = roofSlab.shape.points[1]
  const p3 = roofSlab.shape.points[2]
  const p4 = roofSlab.shape.points[3]

  const baseAxisDirection = p2.sub(p1).normalize()
  const directionP1P4 = p4.sub(p1).normalize()
  const planeNormal = baseAxisDirection.cross(directionP1P4).normalize()
  const upwardsDirection = planeNormal.cross(baseAxisDirection).normalize()
  const distanceP1ToP4Upwards = signedDistanceInDirectionOf(p1, p4, upwardsDirection)

  const A1 = p1.add(baseAxisDirection.multiplyScalar(offsetStart))

  const distanceP1ToP4AlongBase = signedDistanceInDirectionOf(p1, p4, baseAxisDirection)

  if (distanceP1ToP4AlongBase > minPositionWidth) {
    const distanceP1ToB = distanceP1ToP4AlongBase - spacingHalf
    const B1 = p1.add(baseAxisDirection.multiplyScalar(distanceP1ToB))

    const C1Ratio = distanceP1ToB / distanceP1ToP4AlongBase
    const C1 = interpolate(p1, p4, C1Ratio)
    const D1Ratio = offsetStart / distanceP1ToP4AlongBase
    const D1 = interpolate(p1, p4, D1Ratio)
    const points1 = [A1, B1, C1, D1]

    const points1XYRounded8Decimals = roundPointsXY(points1)
    slabsToReturn.push(createVerticalRoofSlabPayload(points1XYRounded8Decimals))
  } else if (distanceP1ToP4AlongBase < -minPositionWidth) {
    const A1 = p1
      .add(upwardsDirection.multiplyScalar(distanceP1ToP4Upwards))
      .add(baseAxisDirection.multiplyScalar(-spacingHalf))
    const B1 = p1
      .add(upwardsDirection.multiplyScalar(distanceP1ToP4Upwards))
      .add(baseAxisDirection.multiplyScalar((Math.abs(distanceP1ToP4AlongBase) - offsetStart) * -1))
    const C1Ratio =
      signedDistanceInDirectionOf(p4, B1, baseAxisDirection) /
      signedDistanceInDirectionOf(p4, p1, baseAxisDirection)
    const C1 = interpolate(p4, p1, C1Ratio)
    const D1Ratio =
      signedDistanceInDirectionOf(p4, A1, baseAxisDirection) /
      signedDistanceInDirectionOf(p4, p1, baseAxisDirection)
    const D1 = interpolate(p4, p1, D1Ratio)
    const points1 = [A1, B1, C1, D1]
    const points1XYRounded8Decimals = roundPointsXY(points1)
    slabsToReturn.push(createVerticalRoofSlabPayload(points1XYRounded8Decimals))
  }

  const distanceP1ToP3AlongBase = signedDistanceInDirectionOf(p1, p3, baseAxisDirection)
  const distanceP1ToP2AlongBase = signedDistanceInDirectionOf(p1, p2, baseAxisDirection)

  const distanceForB2 = Math.min(distanceP1ToP2AlongBase, distanceP1ToP3AlongBase)
  const p3AtBase = p1.add(baseAxisDirection.multiplyScalar(distanceP1ToP3AlongBase))
  const A2 =
    distanceP1ToP4AlongBase > 0
      ? p1.add(baseAxisDirection.multiplyScalar(distanceP1ToP4AlongBase + spacingHalf))
      : p1.add(baseAxisDirection.multiplyScalar(spacingHalf))
  const B2 = p1.add(baseAxisDirection.multiplyScalar(distanceForB2 - spacingHalf))
  const C2 = B2.add(upwardsDirection.multiplyScalar(distanceP1ToP4Upwards))
  const D2 = A2.add(upwardsDirection.multiplyScalar(distanceP1ToP4Upwards))

  const points2 = [A2, B2, C2, D2]
  const points2XYRounded8Decimals = roundPointsXY(points2)
  slabsToReturn.push(createVerticalRoofSlabPayload(points2XYRounded8Decimals))

  const distanceP3ToP2AlongBase = signedDistanceInDirectionOf(p3, p2, baseAxisDirection)

  if (distanceP3ToP2AlongBase > minPositionWidth) {
    const A3 = p1.add(baseAxisDirection.multiplyScalar(distanceP1ToP3AlongBase + spacingHalf))
    const B3 = p2.add(baseAxisDirection.multiplyScalar(offsetEnd * -1))
    const C3Ratio = B3.distanceTo(p2) / p3AtBase.distanceTo(p2)
    const C3 = interpolate(p2, p3, C3Ratio)
    const D3Ratio = A3.distanceTo(p2) / p3AtBase.distanceTo(p2)
    const D3 = interpolate(p2, p3, D3Ratio)
    const points3 = [A3, B3, C3, D3]
    const points3XYRounded8Decimals = roundPointsXY(points3)
    slabsToReturn.push(createVerticalRoofSlabPayload(points3XYRounded8Decimals))
  } else if (distanceP3ToP2AlongBase < -minPositionWidth) {
    const A3 = p3.add(baseAxisDirection.multiplyScalar(offsetEnd * -1))
    const B3 = p3.add(baseAxisDirection.multiplyScalar(distanceP3ToP2AlongBase + spacingHalf))
    const C3Ratio =
      signedDistanceInDirectionOf(B3, p3, baseAxisDirection) /
      signedDistanceInDirectionOf(p2, p3, baseAxisDirection)
    const C3 = interpolate(p3, p2, C3Ratio)
    const D3Ratio =
      signedDistanceInDirectionOf(A3, p3, baseAxisDirection) /
      signedDistanceInDirectionOf(p2, p3, baseAxisDirection)
    const D3 = interpolate(p3, p2, D3Ratio)
    const points3 = [A3, B3, C3, D3]
    const points3XYRounded8Decimals = roundPointsXY(points3)
    slabsToReturn.push(createVerticalRoofSlabPayload(points3XYRounded8Decimals))
  }

  return slabsToReturn
}

// Main component that provides form context
const GeneralizedPositionCreation = ({ selectedElement }: Props): ReactElement => {
  const { projectId } = useParams()
  const roofSlabs = useModelStore(state => state.model.roof_slabs)
  const roofSlab = useMemo(
    () => find(roofSlabs, { guid: selectedElement }),
    [roofSlabs, selectedElement],
  )

  const invalidateResults = useResultsInvalidation()

  const defaultValues = useMemo(
    () => ({
      shape: {
        points: roofSlab?.shape.points || [],
      },
      offset: DEFAULT_OFFSET,
      spacing: DEFAULT_SPACING,
      minPositionWidth: DEFAULT_MIN_POSITION_WIDTH,
    }),
    [roofSlab],
  )

  const { mutateAsync, isLoading } = useMutation(
    async (data: RoofSlabFormData) => {
      if (!roofSlab) return

      const roofSlabWithShape = {
        ...roofSlab,
        shape: data.shape,
      } as ShapeObject

      const verticalRoofSlabs =
        data.shape.points.length === 3
          ? createHipRoofShortSidePositions(
              roofSlabWithShape,
              data.offset,
              data.offset,
              data.spacing,
            )
          : createGeneralizedPositions(
              roofSlabWithShape,
              data.offset,
              data.offset,
              data.spacing,
              data.minPositionWidth,
            )

      // Execute requests sequentially instead of in parallel
      for (const verticalRoofSlab of verticalRoofSlabs) {
        await createVerticalRoofSlab.request(projectId as string, verticalRoofSlab)
      }
    },
    {
      onSuccess: async () => {
        await Promise.all([
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getVerticalTransmissionGraph.getKey(projectId)),
          client.invalidateQueries(getModel.getKey(projectId)),
          client.invalidateQueries(getElementLoads.getKey(projectId)),
        ])
        invalidateResults(projectId as string)
        enqueueSnackbar('Positionen erfolgreich erzeugt', { variant: 'success' })
      },
      onError: (error: AxiosError) => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Erzeugen der Positionen'), {
          variant: 'error',
        })
      },
    },
  )

  // Form submission handler with validation
  const onSubmit = (data: RoofSlabFormData) => {
    if (roofSlab) {
      mutateAsync(data)
    }
  }

  return (
    <Form
      key={selectedElement}
      onSubmit={onSubmit}
      defaultValues={defaultValues}
      validationSchema={stdGeneralizedPositionSchema}
      data-cy="generalized-position-creation-form"
    >
      <Stack direction="column" spacing={1} alignItems="center" width="100%" paddingTop={1}>
        <UnitConvertedField
          name="offset"
          inputLabel="Abstand Start / Ende bei nicht rechteckigen Flächen"
          conversionFactor={1}
          unitLabel="m"
          data-cy="input-generalized-position-offset"
        />
        <Stack direction="row" spacing={1} alignItems="center" width="100%" padding={0}>
          <UnitConvertedField
            name="spacing"
            inputLabel="Zwischenabstand"
            conversionFactor={1}
            unitLabel="m"
            data-cy="input-generalized-position-spacing"
          />
          <UnitConvertedField
            name="minPositionWidth"
            inputLabel="Mindestbreite"
            conversionFactor={1}
            unitLabel="m"
            data-cy="input-generalized-position-min-width"
          />
        </Stack>
        <MagicButton
          type="submit"
          loading={isLoading}
          disabled={false}
          data-cy="btn-create-generalized-positions"
          fullWidth
        >
          Dachpositionen erzeugen
        </MagicButton>
        <Errors />
      </Stack>
    </Form>
  )
}

export default GeneralizedPositionCreation
